import { useState, useEffect } from "react";
import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { AdminNavigation } from "@/components/AdminNavigation";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { FileText, Download, CheckCircle2, BarChart3, ListChecks, FileCode, Users, Shield, Database, Trash2, HardDrive } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { Link } from "react-router-dom";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";

interface ComplianceTask {
  id: string;
  title: string;
  description: string;
  category: string;
  completed: boolean;
  priority: "high" | "medium" | "low";
  recurring?: boolean;
  recurringMonths?: number;
  nextDueDate?: string;
}

const AdminDocuments = () => {
  const { toast } = useToast();
  const [complianceTasks, setComplianceTasks] = useState<ComplianceTask[]>([]);
  const initialComplianceTasks: ComplianceTask[] = [
    {
      id: "vv-1",
      title: "Verarbeitungsverzeichnis erstellen",
      description: "Alle Datenverarbeitungstätigkeiten nach Art. 30 DSGVO dokumentieren",
      category: "Verarbeitungsverzeichnis",
      completed: false,
      priority: "high"
    },
    {
      id: "vv-2",
      title: "Verarbeitungsverzeichnis aktualisieren",
      description: "Quartalsweise Überprüfung und Aktualisierung des Verzeichnisses",
      category: "Verarbeitungsverzeichnis",
      completed: false,
      priority: "medium",
      recurring: true,
      recurringMonths: 3
    },
    {
      id: "avv-1",
      title: "AVV mit Google (Analytics) abschließen",
      description: "Auftragsverarbeitungsvertrag für Google Analytics unterzeichnen",
      category: "Auftragsverarbeitung",
      completed: true,
      priority: "high"
    },
    {
      id: "avv-3",
      title: "AVV mit Supabase (Lovable Cloud) abschließen",
      description: "Data Processing Agreement mit Supabase überprüfen",
      category: "Auftragsverarbeitung",
      completed: true,
      priority: "high"
    },
    {
      id: "avv-4",
      title: "AVV mit World4You (SMTP) abschließen",
      description: "Auftragsverarbeitungsvertrag mit E-Mail-Provider",
      category: "Auftragsverarbeitung",
      completed: true,
      priority: "high"
    },
    {
      id: "avv-5",
      title: "Social Media Links dokumentieren",
      description: "LinkedIn und YouTube Links in Datenschutzerklärung aufnehmen",
      category: "Auftragsverarbeitung",
      completed: true,
      priority: "low"
    },
    {
      id: "betr-1",
      title: "Prozess für Auskunftsanfragen definieren",
      description: "Workflow und Verantwortlichkeiten für Art. 15 DSGVO festlegen",
      category: "Betroffenenrechte",
      completed: false,
      priority: "high"
    },
    {
      id: "betr-2",
      title: "Prozess für Löschanfragen definieren",
      description: "Workflow und Verantwortlichkeiten für Art. 17 DSGVO festlegen",
      category: "Betroffenenrechte",
      completed: false,
      priority: "high"
    },
    {
      id: "betr-3",
      title: "Kontaktadresse für Betroffenenrechte einrichten",
      description: "Dedizierte E-Mail-Adresse (z.B. datenschutz@eap-abutments.com)",
      category: "Betroffenenrechte",
      completed: false,
      priority: "medium"
    },
    {
      id: "dsfa-1",
      title: "DSFA-Bedarf prüfen",
      description: "Bewertung, ob aktuelle Verarbeitungen eine DSFA erfordern",
      category: "Folgenabschätzung",
      completed: false,
      priority: "low"
    },
    {
      id: "schulung-1",
      title: "Datenschutz-Schulung für Mitarbeiter",
      description: "Jährliche Schulung zu DSGVO-Grundlagen und Pflichten",
      category: "Schulung",
      completed: false,
      priority: "medium"
    },
    {
      id: "schulung-2",
      title: "Schulungsunterlagen erstellen",
      description: "Interne Dokumentation und Präsentationen vorbereiten",
      category: "Schulung",
      completed: false,
      priority: "low"
    },
    {
      id: "backup-1",
      title: "Backup-Konzept dokumentieren",
      description: "Backup-Strategie und -Routinen schriftlich festhalten",
      category: "Backup & Löschung",
      completed: true,
      priority: "high"
    },
    {
      id: "backup-2",
      title: "Löschkonzept dokumentieren",
      description: "Aufbewahrungsfristen und Löschroutinen definieren",
      category: "Backup & Löschung",
      completed: true,
      priority: "high"
    },
    {
      id: "backup-3",
      title: "Backup-Test durchführen",
      description: "Quartalsweise Wiederherstellungstest aus Backup",
      category: "Backup & Löschung",
      completed: false,
      priority: "medium"
    }
  ];

  useEffect(() => {
    const savedTasks = localStorage.getItem("dsgvo-compliance-tasks");
    if (savedTasks) {
      setComplianceTasks(JSON.parse(savedTasks));
    } else {
      setComplianceTasks(initialComplianceTasks);
    }
  }, []);

  const toggleTask = (taskId: string) => {
    const task = complianceTasks.find(t => t.id === taskId);
    
    let updatedTasks = complianceTasks.map(t =>
      t.id === taskId ? { ...t, completed: !t.completed } : t
    );

    // If task is recurring and being marked as completed, create next occurrence
    if (task && task.recurring && !task.completed && task.recurringMonths) {
      const nextDueDate = new Date();
      nextDueDate.setMonth(nextDueDate.getMonth() + task.recurringMonths);
      
      const newTask: ComplianceTask = {
        ...task,
        id: `${task.id}-${Date.now()}`,
        completed: false,
        nextDueDate: nextDueDate.toISOString(),
        description: `${task.description} (Fällig: ${nextDueDate.toLocaleDateString('de-DE')})`
      };
      
      updatedTasks = [...updatedTasks, newTask];
      
      toast({
        title: "Neue Aufgabe erstellt",
        description: `Nächste Aktualisierung fällig am ${nextDueDate.toLocaleDateString('de-DE')}`,
      });
    }
    
    setComplianceTasks(updatedTasks);
    localStorage.setItem("dsgvo-compliance-tasks", JSON.stringify(updatedTasks));
    
    toast({
      title: "Status aktualisiert",
      description: "Compliance-Status wurde gespeichert.",
    });
  };

  const resetTasks = () => {
    setComplianceTasks(initialComplianceTasks);
    localStorage.setItem("dsgvo-compliance-tasks", JSON.stringify(initialComplianceTasks));
    toast({
      title: "Zurückgesetzt",
      description: "Alle Aufgaben wurden auf Standardwerte zurückgesetzt.",
    });
  };

  const documents = [
    {
      title: "Datenschutzerklärung",
      description: "DSGVO-konforme Datenschutzerklärung mit allen notwendigen Angaben",
      url: "/datenschutz",
      filename: "Datenschutzerklaerung_EAP.pdf",
      status: "compliant"
    },
    {
      title: "Impressum",
      description: "Vollständiges Impressum gemäß Mediengesetz und E-Commerce-Gesetz",
      url: "/impressum",
      filename: "Impressum_EAP.pdf",
      status: "compliant"
    },
    {
      title: "Allgemeine Geschäftsbedingungen",
      description: "AGB für Geschäftskunden (Zahnärzte und Zahntechniker)",
      url: "/agb",
      filename: "AGB_EAP.pdf",
      status: "compliant"
    },
    {
      title: "Produktbroschüre 2025",
      description: "Technische Dokumentation und klinische Fälle",
      url: "/downloads/eap-broschuere-2025.pdf",
      filename: "EAP_Broschuere_2025.pdf",
      status: "available"
    },
    {
      title: "Betroffenenrechte",
      description: "Prozesse und Vorlagen für DSGVO-Betroffenenrechte (Art. 15-22)",
      url: "/admin/betroffenenrechte",
      filename: "Betroffenenrechte_EAP.pdf",
      status: "compliant"
    }
  ];

  const templates = [
    {
      title: "Verarbeitungsverzeichnis (ausgefüllt)",
      description: "Vollständig ausgefülltes Verarbeitungsverzeichnis für EAP - Art. 30 DSGVO",
      icon: Database,
      link: "/admin/verarbeitungsverzeichnis",
      content: `# Verarbeitungsverzeichnis nach Art. 30 DSGVO

## 1. Name und Kontaktdaten des Verantwortlichen
EAP® Produktions- und Patentverwertungs-GmbH
Gewerbepark Süd 8
8114 Friesach

## 2. Zwecke der Verarbeitung

### Verarbeitung: Kontaktanfragen über Website
- **Zweck**: Beantwortung von Kundenanfragen
- **Kategorien betroffener Personen**: Interessenten, Zahnärzte, Zahntechniker
- **Kategorien personenbezogener Daten**: Name, E-Mail-Adresse, Telefonnummer, Nachrichtentext
- **Kategorien von Empfängern**: Interne Vertriebsmitarbeiter
- **Rechtsgrundlage**: Art. 6 Abs. 1 lit. b DSGVO (vorvertragliche Maßnahmen)
- **Löschfristen**: 3 Jahre nach letztem Kontakt
- **Technische und organisatorische Maßnahmen**: SSL-Verschlüsselung, Zugriffsbeschränkung

### Verarbeitung: Website-Analytics
- **Zweck**: Verbesserung der Website-Nutzererfahrung
- **Kategorien betroffener Personen**: Website-Besucher
- **Kategorien personenbezogener Daten**: IP-Adresse (anonymisiert), Nutzungsverhalten
- **Kategorien von Empfängern**: Google Ireland Limited
- **Rechtsgrundlage**: Art. 6 Abs. 1 lit. a DSGVO (Einwilligung via Cookie-Banner)
- **Löschfristen**: 14 Monate
- **Technische und organisatorische Maßnahmen**: IP-Anonymisierung, Auftragsverarbeitungsvertrag mit Google`
    },
    {
      title: "Auftragsverarbeitungsverträge (ausgefüllt)",
      description: "Vollständige Dokumentation aller AVV mit Google, Facebook, Supabase, Resend etc.",
      icon: FileCode,
      link: "/admin/auftragsverarbeitung",
      content: `# Checkliste: Auftragsverarbeitungsvertrag (AVV)

## Pflichtangaben gemäß Art. 28 DSGVO

- [ ] Name und Kontaktdaten des Auftragsverarbeiters
- [ ] Name und Kontaktdaten des Verantwortlichen
- [ ] Gegenstand und Dauer der Verarbeitung
- [ ] Art und Zweck der Verarbeitung
- [ ] Art der personenbezogenen Daten
- [ ] Kategorien betroffener Personen

## Pflichten des Auftragsverarbeiters

- [ ] Weisungsgebundenheit bestätigt
- [ ] Vertraulichkeitsverpflichtung der Mitarbeiter
- [ ] Technische und organisatorische Maßnahmen (TOMs) dokumentiert
- [ ] Unterauftragnehmer nur mit Genehmigung
- [ ] Unterstützung bei Betroffenenrechten zugesichert
- [ ] Löschung/Rückgabe der Daten nach Vertragsende
- [ ] Informationspflichten bei Datenschutzverletzungen
- [ ] Auditrechte und Kontrollmöglichkeiten vereinbart

## Wichtige Drittanbieter für EAP

### Google Analytics 4
- Status: AVV verfügbar unter https://business.safety.google/adsprocessorterms/
- [ ] AVV unterzeichnet
- [ ] Datenübermittlung USA geprüft (Standard Contract Clauses)

### Facebook Pixel
- Status: AVV verfügbar über Business Manager
- [ ] AVV unterzeichnet  
- [ ] Datenübermittlung USA geprüft (Standard Contract Clauses)

### Hosting-Provider
- [ ] AVV vorhanden
- [ ] EU-Serverstandort bestätigt`
    },
    {
      title: "Prozess Betroffenenrechte",
      description: "Workflow zur Bearbeitung von Auskunfts-, Lösch- und Korrekturanfragen",
      icon: Users,
      content: `# Prozess für Betroffenenanfragen

## Übersicht Betroffenenrechte

1. **Auskunftsrecht** (Art. 15 DSGVO)
2. **Recht auf Berichtigung** (Art. 16 DSGVO)
3. **Recht auf Löschung** (Art. 17 DSGVO)
4. **Recht auf Einschränkung** (Art. 18 DSGVO)
5. **Recht auf Datenübertragbarkeit** (Art. 20 DSGVO)
6. **Widerspruchsrecht** (Art. 21 DSGVO)

## Workflow

### 1. Eingang der Anfrage
- E-Mail an: [datenschutz@eap-abutments.com]
- Postadresse: EAP® GmbH, Gewerbepark Süd 8, 8114 Friesach
- Erfassung in internem System mit Zeitstempel

### 2. Identitätsprüfung
- Bei E-Mail: Rückfrage zur Bestätigung der Identität
- Bei Post: Kopie des Ausweises anfordern
- Dokumentation der Identitätsprüfung

### 3. Bearbeitung
**Frist: 1 Monat** (Verlängerung auf 3 Monate bei komplexen Anfragen möglich)

#### Auskunftsanfrage:
- Welche Daten sind gespeichert? → Kontaktformular-Einträge, Analytics-Daten
- Zweck der Verarbeitung? → Siehe Verarbeitungsverzeichnis
- An wen wurden Daten weitergegeben? → Google (Analytics), Facebook (Pixel)
- Wie lange werden Daten gespeichert? → Siehe Aufbewahrungsfristen
- Antwort per verschlüsselter E-Mail oder Post

#### Löschanfrage:
- Prüfung: Bestehen gesetzliche Aufbewahrungspflichten? (BAO § 132: 7 Jahre)
- Wenn nein: Löschung in allen Systemen
- Wenn ja: Einschränkung der Verarbeitung statt Löschung
- Bestätigung an betroffene Person senden

#### Berichtigungsanfrage:
- Korrektur in allen relevanten Systemen
- Benachrichtigung der Empfänger (wenn möglich)
- Bestätigung an betroffene Person

### 4. Dokumentation
- Alle Anfragen und Maßnahmen protokollieren
- Aufbewahrung der Dokumentation: 3 Jahre
- Vorlage für Nachweis gegenüber Aufsichtsbehörde`
    },
    {
      title: "Datenschutz-Folgenabschätzung",
      description: "Leitfaden zur Durchführung einer DSFA bei risikoreichen Verarbeitungen",
      icon: Shield,
      content: `# Datenschutz-Folgenabschätzung (DSFA)

## Wann ist eine DSFA erforderlich?

Eine DSFA ist durchzuführen bei Verarbeitungen, die voraussichtlich ein **hohes Risiko** für die Rechte und Freiheiten natürlicher Personen zur Folge haben.

### Indikatoren für hohes Risiko:
- Systematische umfangreiche Bewertung persönlicher Aspekte (Profiling)
- Umfangreiche Verarbeitung besonderer Kategorien von Daten
- Systematische umfangreiche Überwachung öffentlich zugänglicher Bereiche
- Innovative Nutzung oder Anwendung neuer technologischer Lösungen

### Für EAP-Website:
**Aktuell: Keine DSFA erforderlich**
- Kontaktformular: Standardverarbeitung, kein hohes Risiko
- Analytics: Anonymisierte Daten, Opt-in via Cookie-Banner
- Keine sensiblen Gesundheitsdaten von Patienten

**Zukünftig erforderlich bei:**
- Einführung eines Online-Portals für Patientendaten
- Automatisierte Entscheidungsfindung
- Biometrische Datenverarbeitung

## DSFA-Prozess (falls erforderlich)

### 1. Systematische Beschreibung
- Zwecke der Verarbeitung
- Berechtigte Interessen
- Beschreibung der Verarbeitungsvorgänge
- Kategorien personenbezogener Daten
- Empfänger der Daten
- Speicherdauer

### 2. Notwendigkeit und Verhältnismäßigkeit
- Sind die Verarbeitungsvorgänge zur Zweckerreichung geeignet?
- Sind sie erforderlich (kein milderes Mittel)?
- Stehen sie in angemessenem Verhältnis zum Zweck?

### 3. Risikobewertung
Bewertung von Risiken für Betroffene:
- Eintrittswahrscheinlichkeit: gering / mittel / hoch
- Schwere der Folgen: gering / mittel / hoch
- Risikomatrix erstellen

### 4. Abhilfemaßnahmen
- Technische Maßnahmen (Verschlüsselung, Pseudonymisierung)
- Organisatorische Maßnahmen (Schulungen, Zugriffskontrollen)
- Datenschutzfreundliche Voreinstellungen
- Transparenzmaßnahmen

### 5. Dokumentation
- Gesamtdokumentation der DSFA aufbewahren
- Bei Bedarf Aufsichtsbehörde konsultieren
- Regelmäßige Überprüfung und Aktualisierung`
    },
    {
      title: "Lösch- und Backup-Konzept",
      description: "Dokumentation der Aufbewahrungsfristen und Backup-Management",
      icon: HardDrive,
      link: "/admin/backup",
      content: `# Lösch- und Backup-Konzept

## Aufbewahrungsfristen

### Kontaktformular-Daten
- **Aufbewahrungsfrist**: 3 Jahre nach letztem Kontakt
- **Rechtsgrundlage**: Verjährungsfristen gemäß ABGB
- **Löschroutine**: Automatische Löschung nach Ablauf der Frist
- **Verantwortlich**: IT-Administration

### Analytics-Daten (Google Analytics)
- **Aufbewahrungsfrist**: 14 Monate
- **Einstellung**: In Google Analytics konfiguriert
- **Automatische Löschung**: Ja

### Rechnungsdaten (falls zukünftig erfasst)
- **Aufbewahrungsfrist**: 7 Jahre
- **Rechtsgrundlage**: § 132 BAO (Bundesabgabenordnung)
- **Keine Löschung vor Ablauf**: Gesetzliche Pflicht

## Backup-Strategie

### Tägliche Backups
- **Was**: Datenbank-Snapshots (Kontaktformular-Einträge)
- **Wo**: Verschlüsselter Cloud-Storage (EU-Region)
- **Aufbewahrung**: 30 Tage
- **Verschlüsselung**: AES-256

### Wöchentliche Backups
- **Was**: Vollständige System-Backups
- **Wo**: Externe Festplatte (vor Ort) + Cloud
- **Aufbewahrung**: 12 Wochen
- **Test der Wiederherstellung**: Quartalsweise

### Monatliche Archivierung
- **Was**: Compliance-relevante Daten
- **Wo**: Archiv-Storage (WORM - Write Once Read Many)
- **Aufbewahrung**: 7 Jahre
- **Zugriff**: Nur für autorisierte Personen

## Löschprozess

### Routinemäßige Löschung
1. Monatliche Prüfung auf abgelaufene Fristen
2. Automatisiertes Skript markiert zu löschende Daten
3. Manuelle Freigabe durch Datenschutzbeauftragten
4. Unwiderrufliche Löschung
5. Dokumentation der Löschung

### Löschung auf Anfrage (siehe Prozess Betroffenenrechte)
1. Identitätsprüfung
2. Prüfung gesetzlicher Aufbewahrungspflichten
3. Löschung oder Einschränkung der Verarbeitung
4. Bestätigung an Betroffenen

## Verantwortlichkeiten
- **Datenschutzbeauftragter**: Überwachung der Einhaltung
- **IT-Administration**: Technische Umsetzung
- **Geschäftsführung**: Letztverantwortung

## Notfallplan bei Datenverlust
1. Sofortige Meldung an Geschäftsführung
2. Prüfung auf Meldepflicht gegenüber Aufsichtsbehörde (72 Stunden)
3. Wiederherstellung aus Backup
4. Ursachenanalyse und Dokumentation
5. Präventivmaßnahmen implementieren`
    },
    {
      title: "Betroffenenrechte Prozesse & Vorlagen",
      description: "Vollständige Dokumentation aller DSGVO-Betroffenenrechte (Art. 15-22) mit Prozessen und E-Mail-Vorlagen",
      icon: Users,
      link: "/admin/betroffenenrechte",
      content: `# Betroffenenrechte nach DSGVO

## Art. 15 DSGVO - Auskunftsrecht
**Frist:** 1 Monat (unverzüglich)

### Prozess:
1. Identität der anfragenden Person prüfen
2. Alle gespeicherten Daten zusammenstellen
3. Verarbeitungszwecke dokumentieren
4. Empfänger und Speicherdauer angeben
5. Über Rechte (Berichtigung, Löschung, etc.) informieren
6. Antwort in verständlicher Form übermitteln

### E-Mail-Vorlage:
Sehr geehrte/r [Name],

vielen Dank für Ihre Anfrage nach Art. 15 DSGVO.

Im Folgenden erhalten Sie Auskunft über Ihre bei uns gespeicherten personenbezogenen Daten:

**Gespeicherte Daten:**
- Name: [Daten]
- E-Mail-Adresse: [Daten]
- [Weitere Daten]

Mit freundlichen Grüßen
EAP® Produktions- und Patentverwertungs-GmbH

---

## Art. 17 DSGVO - Recht auf Löschung
**Frist:** Unverzüglich

### Prozess:
1. Löschanfrage prüfen
2. Gesetzliche Aufbewahrungsfristen prüfen
3. Bei berechtigtem Interesse: Daten löschen
4. Empfänger der Daten über Löschung informieren
5. Löschung bestätigen
6. Bei Ablehnung: Gründe mitteilen

Weitere Betroffenenrechte siehe detaillierte Dokumentation.`
    }
  ];

  const handleDownloadPDF = async (url: string, filename: string) => {
    if (url.startsWith('/downloads/')) {
      // Direct download for existing PDF files
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      return;
    }

    // For HTML pages, open in new window for browser's print to PDF
    window.open(url, '_blank');
  };

  const complianceChecks = [
    {
      label: "Verantwortlicher benannt",
      status: true,
      detail: "EAP® Produktions- und Patentverwertungs-GmbH"
    },
    {
      label: "Betroffenenrechte dokumentiert",
      status: true,
      detail: "Auskunft, Berichtigung, Löschung, Einschränkung, Widerspruch"
    },
    {
      label: "Rechtsgrundlage für Verarbeitung",
      status: true,
      detail: "Art. 6 Abs. 1 DSGVO"
    },
    {
      label: "Cookie-Consent implementiert",
      status: true,
      detail: "Opt-in Banner mit Ablehnung-Option"
    },
    {
      label: "Drittanbieter dokumentiert",
      status: true,
      detail: "Google Analytics 4, Facebook Pixel, Supabase, World4You (SMTP)"
    },
    {
      label: "SSL-Verschlüsselung",
      status: true,
      detail: "HTTPS aktiv"
    },
    {
      label: "Aufbewahrungsfristen definiert",
      status: true,
      detail: "7 Jahre gemäß § 132 BAO"
    },
    {
      label: "Aufsichtsbehörde angegeben",
      status: true,
      detail: "Österreichische Datenschutzbehörde"
    }
  ];

  const downloadTemplate = (template: typeof templates[0]) => {
    const blob = new Blob([template.content], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${template.title.replace(/\s+/g, '_')}.md`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    toast({
      title: "Download gestartet",
      description: `${template.title} wurde heruntergeladen.`,
    });
  };

  const categories = Array.from(new Set(complianceTasks.map(t => t.category)));
  const completedCount = complianceTasks.filter(t => t.completed).length;
  const totalCount = complianceTasks.length;
  const completionPercentage = Math.round((completedCount / totalCount) * 100);

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      <AdminNavigation />
      <main className="pt-24 pb-24">
        <div className="container mx-auto px-4 max-w-6xl">
          <Breadcrumbs items={[{ label: "Admin" }, { label: "Dokumente" }]} />
          
          <div className="mb-8">
            <div className="flex flex-col items-start justify-between gap-4 mb-4 sm:flex-row sm:items-center">
              <div>
                <h1 className="break-words text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mb-2">
                  DSGVO-Compliance Center
                </h1>
                <p className="text-xl text-muted-foreground">
                  Dokumente, Templates & Compliance-Management
                </p>
              </div>
              <Button variant="outline" onClick={resetTasks}>
                Aufgaben zurücksetzen
              </Button>
            </div>
          </div>

          <Tabs defaultValue="overview" className="w-full">
            <TabsList className="flex h-auto w-full justify-start overflow-x-auto mb-8 p-1 [scrollbar-width:thin]">
              <TabsTrigger value="overview" className="flex shrink-0 items-center gap-2">
                <CheckCircle2 className="w-4 h-4" />
                Übersicht
              </TabsTrigger>
              <TabsTrigger value="tasks" className="flex shrink-0 items-center gap-2">
                <ListChecks className="w-4 h-4" />
                Aufgaben
              </TabsTrigger>
              <TabsTrigger value="templates" className="flex shrink-0 items-center gap-2">
                <FileText className="w-4 h-4" />
                Templates
              </TabsTrigger>
              <TabsTrigger value="documents" className="flex shrink-0 items-center gap-2">
                <Download className="w-4 h-4" />
                Dokumente
              </TabsTrigger>
            </TabsList>

            {/* Overview Tab */}
            <TabsContent value="overview">
              <div className="space-y-6">
                {/* Progress Card */}
                <Card className="p-6 border-2 border-primary/20">
                  <div className="flex items-start gap-4 mb-6">
                    <div className="p-3 bg-primary/10 rounded-lg">
                      <Shield className="w-8 h-8 text-primary" />
                    </div>
                          <div className="min-w-0 flex-1">
                      <h2 className="text-2xl font-semibold text-foreground mb-2">
                        Compliance-Fortschritt
                      </h2>
                      <p className="text-muted-foreground mb-4">
                        {completedCount} von {totalCount} Aufgaben abgeschlossen ({completionPercentage}%)
                      </p>
                      <div className="w-full bg-secondary h-3 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-primary transition-all duration-500"
                          style={{ width: `${completionPercentage}%` }}
                        />
                      </div>
                    </div>
                  </div>
                </Card>

                {/* DSGVO Compliance Status */}
                <Card className="p-6 border-2 border-green-500/20">
                  <div className="flex items-start gap-3 mb-4">
                    <CheckCircle2 className="w-6 h-6 text-green-500 mt-1 flex-shrink-0" />
                    <div>
                      <h2 className="text-2xl font-semibold text-foreground mb-2">
                        DSGVO-Basis-Compliance
                      </h2>
                      <p className="text-muted-foreground mb-4">
                        Ihre Website erfüllt die wesentlichen Anforderungen
                      </p>
                    </div>
                  </div>
                  
                  <div className="grid md:grid-cols-2 gap-3 mt-6">
                    {complianceChecks.map((check, index) => (
                      <div key={index} className="flex items-start gap-2 bg-secondary/30 p-3 rounded-lg">
                        <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="font-medium text-foreground text-sm">{check.label}</p>
                          <p className="text-xs text-muted-foreground">{check.detail}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </Card>

                {/* Quick Actions */}
                <div className="grid md:grid-cols-3 gap-4">
                  <Card className="p-6 hover:shadow-lg transition-shadow cursor-pointer" onClick={() => document.querySelector('[data-value="tasks"]')?.dispatchEvent(new MouseEvent('click', { bubbles: true }))}>
                    <ListChecks className="w-8 h-8 text-primary mb-3" />
                    <h3 className="font-semibold text-foreground mb-2">Aufgaben verwalten</h3>
                    <p className="text-sm text-muted-foreground">
                      {totalCount - completedCount} offene Compliance-Aufgaben
                    </p>
                  </Card>
                  <Card className="p-6 hover:shadow-lg transition-shadow cursor-pointer" onClick={() => document.querySelector('[data-value="templates"]')?.dispatchEvent(new MouseEvent('click', { bubbles: true }))}>
                    <FileText className="w-8 h-8 text-primary mb-3" />
                    <h3 className="font-semibold text-foreground mb-2">Templates nutzen</h3>
                    <p className="text-sm text-muted-foreground">
                      {templates.length} Vorlagen verfügbar
                    </p>
                  </Card>
                  <Card className="p-6 hover:shadow-lg transition-shadow cursor-pointer" onClick={() => document.querySelector('[data-value="documents"]')?.dispatchEvent(new MouseEvent('click', { bubbles: true }))}>
                    <Download className="w-8 h-8 text-primary mb-3" />
                    <h3 className="font-semibold text-foreground mb-2">Dokumente exportieren</h3>
                    <p className="text-sm text-muted-foreground">
                      {documents.length} PDF-Exporte
                    </p>
                  </Card>
                </div>
              </div>
            </TabsContent>

            {/* Tasks Tab */}
            <TabsContent value="tasks">
              <div className="space-y-6">
                <Alert>
                  <ListChecks className="h-4 w-4" />
                  <AlertDescription>
                    Verfolgen Sie Ihren Fortschritt bei der Umsetzung aller DSGVO-Maßnahmen. 
                    Markieren Sie Aufgaben als erledigt - Ihr Status wird automatisch gespeichert.
                  </AlertDescription>
                </Alert>

                {categories.map(category => {
                  const categoryTasks = complianceTasks.filter(t => t.category === category);
                  const categoryCompleted = categoryTasks.filter(t => t.completed).length;
                  
                  return (
                    <Card key={category} className="p-6">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-xl font-semibold text-foreground">{category}</h3>
                        <span className="text-sm text-muted-foreground">
                          {categoryCompleted}/{categoryTasks.length} erledigt
                        </span>
                      </div>
                      
                      <div className="space-y-3">
                        {categoryTasks.map(task => (
                          <div 
                            key={task.id}
                            className={`flex items-start gap-3 p-4 rounded-lg border-2 transition-all ${
                              task.completed 
                                ? 'bg-green-500/5 border-green-500/20' 
                                : 'bg-secondary/30 border-border'
                            }`}
                          >
                            <Checkbox
                              id={task.id}
                              checked={task.completed}
                              onCheckedChange={() => toggleTask(task.id)}
                              className="mt-1"
                            />
                            <div className="flex-1">
                              <label 
                                htmlFor={task.id}
                                className={`font-medium cursor-pointer ${
                                  task.completed ? 'line-through text-muted-foreground' : 'text-foreground'
                                }`}
                              >
                                {task.title}
                              </label>
                              <p className="text-sm text-muted-foreground mt-1">{task.description}</p>
                            </div>
                            <span className={`shrink-0 text-xs px-2 py-1 rounded-full ${
                              task.priority === 'high' ? 'bg-red-500/10 text-red-500' :
                              task.priority === 'medium' ? 'bg-yellow-500/10 text-yellow-500' :
                              'bg-blue-500/10 text-blue-500'
                            }`}>
                              {task.priority === 'high' ? 'Hoch' : task.priority === 'medium' ? 'Mittel' : 'Niedrig'}
                            </span>
                          </div>
                        ))}
                      </div>
                    </Card>
                  );
                })}
              </div>
            </TabsContent>

            {/* Templates Tab */}
            <TabsContent value="templates">
              <div className="space-y-6">
                <Alert>
                  <FileText className="h-4 w-4" />
                  <AlertDescription>
                    Nutzen Sie diese Vorlagen als Ausgangspunkt für Ihre DSGVO-Dokumentation. 
                    Laden Sie sie herunter und passen Sie sie an Ihre spezifischen Bedürfnisse an.
                  </AlertDescription>
                </Alert>

                  <div className="grid gap-6">
                  {templates.map((template, index) => {
                    const Icon = template.icon;
                    return (
                      <Card key={index} className="p-6 hover:shadow-lg transition-shadow">
                        <div className="flex items-start gap-4">
                          <div className="p-3 bg-primary/10 rounded-lg flex-shrink-0">
                            <Icon className="w-6 h-6 text-primary" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="text-xl font-semibold text-foreground mb-2">
                              {template.title}
                            </h3>
                            <p className="text-sm text-muted-foreground mb-4">
                              {template.description}
                            </p>
                            {template.link ? (
                              <div className="flex gap-2">
                                <Button
                                  onClick={() => window.location.href = template.link}
                                  className="bg-primary hover:bg-primary-hover"
                                >
                                  <FileText className="w-4 h-4 mr-2" />
                                  Verarbeitungsverzeichnis öffnen
                                </Button>
                              </div>
                            ) : (
                              <>
                                <div className="bg-secondary/30 p-4 rounded-lg mb-4 max-h-64 overflow-y-auto">
                                  <pre className="text-xs text-foreground whitespace-pre-wrap font-mono">
                                    {template.content.substring(0, 500)}...
                                  </pre>
                                </div>
                                <div className="flex gap-2">
                                  <Button
                                    onClick={() => downloadTemplate(template)}
                                    className="bg-primary hover:bg-primary-hover"
                                  >
                                    <Download className="w-4 h-4 mr-2" />
                                    Template herunterladen
                                  </Button>
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                      </Card>
                    );
                  })}
                </div>
              </div>
            </TabsContent>

            {/* Documents Tab */}
            <TabsContent value="documents">
              <div className="space-y-6">
                <Alert>
                  <FileText className="h-4 w-4" />
                  <AlertDescription>
                    <strong>Hinweis zum PDF-Export:</strong>
                    <ul className="list-disc list-inside mt-2 space-y-1 text-sm">
                      <li>HTML-Dokumente: Öffnet Druckansicht → "Als PDF speichern"</li>
                      <li>Vorhandene PDFs: Direkter Download</li>
                      <li>Archivieren Sie alle PDFs regelmäßig</li>
                    </ul>
                  </AlertDescription>
                </Alert>

                <div className="grid md:grid-cols-2 gap-6">
                  {documents.map((doc, index) => (
                    <Card key={index} className="p-6 hover:shadow-lg transition-shadow">
                      <div className="flex items-start gap-4">
                        <div className="p-3 bg-primary/10 rounded-lg flex-shrink-0">
                          <FileText className="w-6 h-6 text-primary" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="text-xl font-semibold text-foreground mb-2">
                            {doc.title}
                          </h3>
                          <p className="text-sm text-muted-foreground mb-4">
                            {doc.description}
                          </p>
                          <div className="flex flex-wrap gap-2">
                            <Button
                              onClick={() => handleDownloadPDF(doc.url, doc.filename)}
                              className="bg-primary hover:bg-primary-hover"
                            >
                              <Download className="w-4 h-4 mr-2" />
                              {doc.status === 'available' ? 'Download PDF' : 'Als PDF speichern'}
                            </Button>
                            <Button
                              variant="outline"
                              onClick={() => window.open(doc.url, '_blank')}
                            >
                              Ansehen
                            </Button>
                          </div>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default AdminDocuments;
